'use client';

import { createContext, use, useId } from 'react';
import { sanitizeHref } from '@bridge/utils';
import { Slot as SlotPrimitive } from 'radix-ui';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import { tv } from 'tailwind-variants';

import { Label } from '../typography';

import type { ControllerProps, FieldPath, FieldValues } from 'react-hook-form';

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = use(FormFieldContext);
  const itemContext = use(FormItemContext);
  const { getFieldState, formState } = useFormContext();
  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = createContext<FormItemContextValue>({} as FormItemContextValue);

const formItem = tv({
  base: 'grid gap-2',
});

function FormItem({ className, ...props }: React.ComponentProps<'div'>) {
  const id = useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div className={formItem({ className })} data-slot="form-item" {...props} />
    </FormItemContext.Provider>
  );
}

const formLabel = tv({
  base: 'data-[error=true]:text-destructive',
});

function FormLabel({
  className,
  required,
  children,
  ...props
}: React.ComponentProps<typeof Label> & { required?: boolean }) {
  const { error, formItemId } = useFormField();

  return (
    <Label
      className={formLabel({ className })}
      data-error={!!error}
      data-slot="form-label"
      htmlFor={formItemId}
      {...props}
    >
      {children}
      {required && <span className="text-content-negative relative -top-1 text-[0.75em] leading-none">*</span>}
    </Label>
  );
}

function FormControl({ ...props }: React.ComponentProps<typeof SlotPrimitive.Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return (
    <SlotPrimitive.Slot
      aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      data-slot="form-control"
      id={formItemId}
      {...props}
    />
  );
}

const formDescription = tv({
  base: 'text-secondary text-sm',
});

/**
 * Parses a simple subset of markdown for form descriptions.
 * Supports: **bold**, *italic*, [links](url), and `inline code`.
 */
function parseInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let keyIndex = 0;

  const getKey = () => keyIndex++;

  // Regex patterns for inline markdown
  const patterns: {
    regex: RegExp;
    render: (match: RegExpMatchArray, key: number) => React.ReactNode;
  }[] = [
    // Bold: **text**
    { regex: /\*\*(.+?)\*\*/, render: (m, key) => <strong key={key}>{m[1]}</strong> },
    // Italic: *text* (but not inside **)
    { regex: /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/, render: (m, key) => <em key={key}>{m[1]}</em> },
    // Link: [text](url)
    {
      regex: /\[(.+?)\]\((.+?)\)/,
      render: (m, key) => (
        <a
          className="text-content-accent underline hover:text-content-accent-hover"
          href={sanitizeHref(m[2])}
          key={key}
          rel="noopener noreferrer"
          target="_blank"
        >
          {m[1]}
        </a>
      ),
    },
    // Inline code: `code`
    {
      regex: /`(.+?)`/,
      render: (m, key) => (
        <code className="rounded bg-background-muted px-1 py-0.5 font-mono text-xs" key={key}>
          {m[1]}
        </code>
      ),
    },
  ];

  while (remaining) {
    let earliestMatch: { index: number; length: number; node: React.ReactNode } | null = null;

    for (const pattern of patterns) {
      const match = remaining.match(pattern.regex);
      if (match && match.index !== undefined) {
        const matchIndex = match.index;
        if (!earliestMatch || matchIndex < earliestMatch.index) {
          earliestMatch = {
            index: matchIndex,
            length: match[0].length,
            node: pattern.render(match, getKey()),
          };
        }
      }
    }

    if (earliestMatch) {
      // Add text before match
      if (earliestMatch.index > 0) {
        parts.push(remaining.slice(0, earliestMatch.index));
      }
      // Add matched element
      parts.push(earliestMatch.node);
      // Continue with remaining text
      remaining = remaining.slice(earliestMatch.index + earliestMatch.length);
    } else {
      // No more matches, add remaining text
      parts.push(remaining);
      break;
    }
  }

  return parts;
}

interface FormDescriptionProps extends Omit<React.ComponentProps<'p'>, 'children'> {
  children?: React.ReactNode;
  /**
   * Enable markdown parsing for string children.
   * Supports **bold**, *italic*, [links](url), and `code`.
   * Defaults to true for string children.
   */
  markdown?: boolean;
}

function FormDescription({ className, children, markdown = true, ...props }: FormDescriptionProps) {
  const { formDescriptionId } = useFormField();

  const content = markdown && typeof children === 'string' ? parseInlineMarkdown(children) : children;

  return (
    <p className={formDescription({ className })} data-slot="form-description" id={formDescriptionId} {...props}>
      {content}
    </p>
  );
}

const formMessage = tv({
  base: 'text-destructive text-sm',
});

function FormMessage({ className, ...props }: React.ComponentProps<'p'>) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? '') : props.children;

  if (!body) {
    return null;
  }

  return (
    <p className={formMessage({ className })} data-slot="form-message" id={formMessageId} {...props}>
      {body}
    </p>
  );
}

export { useFormField, Form, FormItem, FormLabel, FormControl, FormDescription, FormMessage, FormField };
